import React, { useMemo, useRef, useState } from 'react'
import { Sheet, useTheme, Button, ScrollView, YStack, XStack, Text, Input } from 'tamagui'
import { CobuGradientButton, CobuText, CobuButton } from '@cobu/ui'
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt'
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral'
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied'
import dayjs from 'dayjs'

// Use Tamagui primitives for DS-aligned UI
import { Strings } from '../../constants/Strings'

export interface EventFeedback {
  uuid: string
  isCreator?: boolean
  groupEvent?: { title: string; startTime: string }
  post?: { title: string; startTime: string }
  interestedDate?: string
}

interface EventFeedbackDialogProps {
  visible: boolean
  feedback: EventFeedback
  onFacePress: (uuid: string, rating: number) => void
  onDidNotAttendPress: (uuid: string, type: 'groupEvent' | 'post') => void
  onSubmitEventFeedback: (type: 'groupEvent' | 'post') => void
  generalRating: number | null
  feedbackInputText: string
  onChangeFeedbackInputText: (text: string) => void
  onClose?: () => void
}

const strings = Strings.get()

export default function EventFeedbackDialog(props: EventFeedbackDialogProps) {
  const {
    visible,
    feedback,
    onFacePress,
    onDidNotAttendPress,
    onSubmitEventFeedback,
    generalRating,
    feedbackInputText,
    onChangeFeedbackInputText,
    onClose,
  } = props

  const theme = useTheme()
  const scrollRef = useRef<any>(null)
  const inputRef = useRef<any>(null)
  const [inputY, setInputY] = useState(0)

  const type: 'groupEvent' | 'post' | null = feedback?.groupEvent ? 'groupEvent' : feedback?.post ? 'post' : null
  const event = type ? (feedback as any)[type] : null
  const title = event?.title || ''
  const startTime = event?.startTime || ''

  const { dayText, dateText, timeText } = useMemo(() => {
    const base = feedback?.interestedDate ? dayjs(feedback.interestedDate) : dayjs(startTime)
    return {
      dayText: base.format('ddd').toUpperCase(),
      dateText: base.format('MMM DD').toUpperCase(),
      timeText: base.format('h:mm A').toUpperCase(),
    }
  }, [feedback?.interestedDate, startTime])

  if (!visible || !feedback || !type) return null

  return (
    <Sheet modal open={visible} onOpenChange={(open: boolean) => { if (!open) onClose?.() }} disableDrag>
      <Sheet.Overlay />
      <Sheet.Frame>
        {/* Header */}
        <YStack
          paddingHorizontal="$4"
          paddingTop="$4"
          paddingBottom="$6"
          style={{
            backgroundImage: `linear-gradient(180deg, ${theme.cobuTeal?.val} 0%, ${theme.cobuDarkBlue?.val} 100%)`,
          }}
        >
          <YStack gap="$2">
            <CobuText color="$white" fontSize='$5' fontWeight="300">
              {feedback.isCreator ? strings.events.feedback.header.thanksForOrganizing : strings.events.feedback.header.thanksForComing}
            </CobuText>
            <CobuText color="$white" fontSize='$7' fontWeight="bold" numberOfLines={2}>
              {title}
            </CobuText>
            <XStack gap="$1">
              <CobuText color="$white" fontSize='$2' fontWeight="500">{dayText}</CobuText>
              <CobuText color="$white" fontSize='$2' fontWeight="bold">{dateText}</CobuText>
              <CobuText color="$white" fontSize='$2' fontWeight="500">{timeText}</CobuText>
            </XStack>
          </YStack>
        </YStack>

        <ScrollView ref={scrollRef as any}>
          {/* Rating */}
          <YStack gap="$4" paddingHorizontal="$4" paddingVertical="$3">
            <CobuText color="$colorSecondary" fontSize='$2' fontWeight="600">
              {strings.events.feedback.rating.sectionTitle}
            </CobuText>

            <XStack justifyContent="space-evenly" paddingVertical="$3">
              {[3, 2, 1].map((rating) => (
                <YStack key={rating} alignItems="center" gap="$2">
                  <CobuButton
                    height={'$12'}
                    width={'$14'}
                    padding={0}
                    backgroundColor="transparent"
                    onPress={() => onFacePress(feedback.uuid, rating)}
                  >
                    {rating === 3 && (
                      <SentimentSatisfiedAltIcon
                        htmlColor={
                          generalRating === rating
                            ? theme.cobuTopaz?.val || '#16a34a'
                            : theme.colorMuted?.val || '#9CA3AF'
                        }
                      />
                    )}
                    {rating === 2 && (
                      <SentimentNeutralIcon
                        htmlColor={
                          generalRating === rating
                            ? theme.cobuTopaz?.val || '#16a34a'
                            : theme.colorMuted?.val || '#9CA3AF'
                        }
                      />
                    )}
                    {rating === 1 && (
                      <SentimentDissatisfiedIcon
                        htmlColor={
                          generalRating === rating
                            ? theme.cobuTopaz?.val || '#16a34a'
                            : theme.colorMuted?.val || '#9CA3AF'
                        }
                      />
                    )}
                  </CobuButton>
                  <CobuText
                    color={generalRating === rating ? '$cobuTopaz' : '$color'}
                    fontSize='$2'
                    textAlign="center"
                  >
                    {rating === 3 ? strings.events.feedback.rating.excellent : rating === 2 ? strings.events.feedback.rating.good : strings.events.feedback.rating.meh}
                  </CobuText>
                </YStack>
              ))}
            </XStack>
          </YStack>

          {/* Input */}
          <YStack paddingHorizontal="$4" paddingBottom="$4" gap="$3" onLayout={(e: any) => setInputY(e?.nativeEvent?.layout?.y || 0)}>
            <CobuText color="$color" fontSize='$2' fontWeight="500">
              {strings.events.feedback.comment.sectionTitle}
            </CobuText>
            <div
              onMouseDown={e => {
                e.stopPropagation()
                requestAnimationFrame(() => inputRef.current?.focus?.())
              }}
            >
              <Input
                ref={inputRef}
                value={feedbackInputText}
                onChangeText={onChangeFeedbackInputText}
                multiline
                numberOfLines={4}
                onFocus={() => {
                  requestAnimationFrame(() => {
                    scrollRef.current?.scrollTo?.({ top: Math.max(inputY - 24, 0), behavior: 'smooth' })
                  })
                }}
                height={'$10'}
                color="$color"
                width="100%"
              />
            </div>
          </YStack>
        </ScrollView>

        {/* Action bar */}
        <XStack justifyContent="space-between" alignItems="center" paddingHorizontal="$4" paddingVertical="$3" backgroundColor="$background">
          <CobuButton backgroundColor="$transparent" onPress={() => onDidNotAttendPress(feedback.uuid, type)} height={'$10'}>
            <CobuText color="$color" fontSize='$4' fontWeight="bold">
              {strings.events.feedback.buttons.didNotAttend}
            </CobuText>
          </CobuButton>
         <CobuButton dsVariant="primary"
          title={strings.events.feedback.buttons.submit} onPress={() => generalRating && onSubmitEventFeedback(type)} disabled={!generalRating} width="40%" />
        </XStack>
      </Sheet.Frame>
    </Sheet>
  )
}


